import React from 'react';
import { AbsoluteFill, Audio, Sequence, interpolate, useVideoConfig } from 'remotion';
import type { VideoProps } from './types';
import { computeTimeline, VOICE_DELAY } from './timeline';
import { SceneView } from './scenes';
import { Captions } from './captions';

const clamp = { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' } as const;
const DUCKED = 0.3; // music level, relative, while the narrator speaks

/** The whole video: scenes laid out by computeTimeline, plus music and narration. */
export const Video: React.FC<VideoProps> = ({ spec, assets }) => {
    const { durationInFrames, fps } = useVideoConfig();
    const { scenes } = computeTimeline(spec, assets);
    const voices = spec.voice.enabled
        ? scenes
              .map((t) => ({ t, v: assets?.voice?.[spec.scenes[t.index].id] }))
              .filter((x): x is { t: typeof x.t; v: NonNullable<typeof x.v> } => !!x.v)
        : [];

    const audibleClips = scenes.filter((t) => {
        const s = spec.scenes[t.index];
        return s.type === 'video' && s.muted === false;
    });

    const musicVolume = (f: number) => {
        let duck = 0;
        for (const { t, v } of voices) {
            const a = t.from + VOICE_DELAY;
            const b = a + v.duration * fps;
            duck = Math.max(duck, interpolate(f, [a - 8, a, b, b + 16], [0, 1, 1, 0], clamp));
        }
        // Clips playing their own sound duck the music for the whole scene.
        for (const t of audibleClips) {
            duck = Math.max(duck, interpolate(f, [t.from - 8, t.from, t.from + t.frames, t.from + t.frames + 16], [0, 1, 1, 0], clamp));
        }
        const fade = interpolate(f, [0, 15, durationInFrames - 45, durationInFrames], [0, 1, 1, 0], clamp);
        return spec.music.volume * (1 - (1 - DUCKED) * duck) * fade;
    };

    return (
        <AbsoluteFill style={{ background: spec.theme.background }}>
            {scenes.map((t) => (
                <Sequence key={spec.scenes[t.index].id} from={t.from} durationInFrames={t.frames}>
                    <SceneView scene={spec.scenes[t.index]} ctx={{ spec, frames: t.frames }} first={t.index === 0} />
                </Sequence>
            ))}
            {assets?.musicUrl && spec.music.track !== 'none' && <Audio src={assets.musicUrl} loop volume={musicVolume} />}
            {voices.map(({ t, v }) => (
                <Sequence key={`v-${t.index}`} from={t.from + VOICE_DELAY} durationInFrames={Math.ceil(v.duration * fps) + 4} layout="none">
                    <Audio src={v.url} />
                </Sequence>
            ))}
            {spec.captions?.enabled &&
                voices
                    // A clip with its own speech captions shows those instead.
                    .filter(({ t }) => {
                        const sc = spec.scenes[t.index];
                        return !(sc.type === 'video' && sc.muted === false && sc.subtitles?.length);
                    })
                    .map(({ t, v }) => (
                    <Sequence key={`c-${t.index}`} from={t.from + VOICE_DELAY} durationInFrames={Math.ceil(v.duration * fps) + 8}>
                        <Captions text={spec.scenes[t.index].narration || ''} durationSec={v.duration} />
                    </Sequence>
                ))}
        </AbsoluteFill>
    );
};
